import { CommonModule } from '@angular/common';
import {
  ChangeDetectionStrategy,
  Component,
  OnInit,
  TemplateRef,
  inject,
} from '@angular/core';
import {
  FieldArrayType,
  FieldArrayTypeConfig,
  FormlyFieldConfig,
  FormlyFieldProps,
  FormlyModule,
} from '@ngx-formly/core';
import { Observable } from 'rxjs';
import { ConfirmationService } from 'primeng/api';
import { BadgeModule } from 'primeng/badge';
import { ButtonModule } from 'primeng/button';
import { ConfirmPopupModule } from 'primeng/confirmpopup';
import { TabViewCloseEvent, TabViewModule } from 'primeng/tabview';

interface Props extends FormlyFieldProps {
  labelField?: string;
  addText?: string;
  newItem: (currentItem: any | undefined, idx: number) => any;
  initialIndex?: number;
  onActiveIndexChange?: (index: number) => void;
  tabActionsTemplate?: Observable<TemplateRef<any>>;
}

@Component({
  selector: 'kon-dynamic-form-tabs-array',
  standalone: true,
  imports: [
    CommonModule,
    TabViewModule,
    FormlyModule,
    ButtonModule,
    ConfirmPopupModule,
    BadgeModule,
  ],
  template: `
    <p-confirmPopup></p-confirmPopup>
    <p-tabView
      #tabview
      [(activeIndex)]="activeIndex"
      [controlClose]="true"
      (onClose)="tabRemove($event)"
    >
      <p-tabPanel
        *ngFor="let tab of field.fieldGroup; let i = index; let first = first"
        [closable]="true"
        [selected]="i === activeIndex"
        [headerStyleClass]="tab.formControl?.dirty ? 'tab-dirty' : ''"
      >
        <ng-template pTemplate="header">
          <span>{{
            props.labelField && model[i]
              ? model[i][props.labelField] ?? '#' + i
              : '#' + i
          }}</span>
          <ng-container
            *ngIf="i === activeIndex && props.tabActionsTemplate"
          >
            <ng-container
              *ngTemplateOutlet="(props.tabActionsTemplate | async) || null"
            ></ng-container>
          </ng-container>
        </ng-template>
        <formly-field [field]="tab"> </formly-field>
      </p-tabPanel>
      <p-tabPanel [selected]="false" class="overflow-visible">
        <ng-template pTemplate="header">
          <p-button
            icon="pi pi-plus"
            [rounded]="true"
            [text]="true"
            styleClass="p-button-text"
            size="small"
            (click)="tabAdd()"
          ></p-button>
        </ng-template>
      </p-tabPanel>
    </p-tabView>
  `,
  styles: `
    ::ng-deep .tab-dirty { position: relative; }
    ::ng-deep .tab-dirty::after {
        content: ' ';
        position: absolute;
        top: 0;
        left: 0;
        background: var(--color-white);
        border: 1px solid var(--input-color-dirty-border);
        width: 1rem;
        height: 1rem;
        border-radius: 50%;
        display: inline-block;
      }
  `,
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class FormlyTabsRepeatTypeComponent
  extends FieldArrayType<FieldArrayTypeConfig<Props>>
  implements OnInit
{
  confirmationService = inject(ConfirmationService);

  override defaultOptions = {
    props: {
      newItem: (current: any) => current ?? {},
    },
  };

  ngOnInit(): void {
    this._activeIndex = this.props?.initialIndex ?? 0;
  }

  tabAdd() {
    const index = this.activeIndex;
    const newItem = this.props.newItem(this.model?.[index], index);
    this.add(undefined, newItem, { markAsDirty: true });
    this.activeIndex = index + 1;
  }

  tabRemove(e: TabViewCloseEvent) {
    this.confirmationService.confirm({
      target: e.originalEvent.currentTarget || undefined,
      message: 'Naozaj chcete odstrániť záznam?',
      accept: () => {
        this.remove(e.index);
      },
      acceptButtonStyleClass: 'p-button-danger',
      rejectButtonStyleClass: 'p-button-secondary p-button-text',
    });
  }

  private _activeIndex = 0;

  get activeIndex(): number {
    return this._activeIndex;
  }

  set activeIndex(i: number) {
    this._activeIndex = i ?? 0;

    if (typeof this.field?.props?.onActiveIndexChange === 'function') {
      this.field.props.onActiveIndexChange(this._activeIndex);
    }
  }

  isValid(field: FormlyFieldConfig): boolean {
    if (field.key) {
      return field.formControl?.valid === true;
    }

    return field.fieldGroup
      ? field.fieldGroup.every((f) => this.isValid(f))
      : true;
  }
}
